import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Form, Spinner } from 'react-bootstrap';
import { Eye, EyeOff } from 'lucide-react';
import { api } from '../../utils/apiClient';
import { showToast } from '../../utils/toast';
import './tools.css';

const PAGE_SIZE = 100;

const HideProductsPage = ({ mode = 'quote' }) => {
  const { t, i18n } = useTranslation();
  const settingsKey = mode === 'cart' ? 'hiddenAddToCartProductIds' : 'hiddenProductIds';
  const ns = mode === 'cart' ? 'tools.hideAddToCart' : 'tools.hideProducts';
  const tKey = (key, opts) => t(`${ns}.${key}`, opts);
  const [categories, setCategories] = useState([]);
  const [loadMode, setLoadMode] = useState('category'); // category | sku
  const [categoryId, setCategoryId] = useState('');
  const [skuPrefix, setSkuPrefix] = useState('');
  const [products, setProducts] = useState([]);
  const [productTotal, setProductTotal] = useState(0);
  const [loadedSource, setLoadedSource] = useState(null);
  const [hiddenIds, setHiddenIds] = useState([]);
  const [draftHidden, setDraftHidden] = useState(() => new Set());
  const [loaded, setLoaded] = useState(false);
  const [filter, setFilter] = useState('all'); // all | visible | hidden
  const [query, setQuery] = useState('');
  const [catalogHits, setCatalogHits] = useState(null);
  const [catalogSearching, setCatalogSearching] = useState(false);
  const catalogSeq = useRef(0);
  const [loading, setLoading] = useState(true);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [saving, setSaving] = useState(false);
  const [buttonSelector, setButtonSelector] = useState('');
  const [pickerUrl, setPickerUrl] = useState('');
  const [savingSelector, setSavingSelector] = useState(false);
  const pickerWindowRef = useRef(null);
  const loadSeq = useRef(0);

  const loadMeta = useCallback(async () => {
    setLoading(true);
    try {
      const [catRes, toolsRes] = await Promise.all([
        api.productCategories(),
        api.getSettingsSection('tools'),
      ]);
      const cats = catRes?.categories || [];
      setCategories(cats);
      const rawIds = toolsRes?.tools?.[settingsKey];
      const ids = Array.isArray(rawIds) ? rawIds.map(String).filter(Boolean) : [];
      setHiddenIds(ids);
      if (mode === 'cart') {
        setButtonSelector(String(toolsRes?.tools?.addToCartButtonSelector || ''));
      }
      setDraftHidden(new Set(ids));
      setCategoryId((prev) => prev || (cats[0]?.categoryId ?? ''));
      if (ids.length) {
        const saved = await api.productsByIds(ids);
        const list = Array.isArray(saved?.products) ? saved.products : [];
        setProducts(list);
        setProductTotal(list.length);
        setLoadedSource({ mode: 'saved' });
        setLoaded(list.length > 0);
      }
    } catch (err) {
      showToast(err.message || tKey('toast.loadFailed'), 'error');
    } finally {
      setLoading(false);
    }
  }, [t, settingsKey, mode]);

  useEffect(() => {
    loadMeta();
  }, [loadMeta]);

  useEffect(() => {
    if (mode !== 'cart') return undefined;
    const onMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      const data = event.data;
      if (!data || data.source !== 'q2c-button-picker') return;
      if (!pickerWindowRef.current || event.source !== pickerWindowRef.current) return;
      const selector = String(data.selector || '').trim();
      if (!selector || selector.length > 400 || selector.includes(',')) return;
      if (/[<>{}]|javascript:/i.test(selector)) return;
      if (/^(button|a|div|span|form)$/i.test(selector)) return;
      try {
        pickerWindowRef.current?.close();
      } catch {
        /* The picker window may already be closed. */
      }
      setSavingSelector(true);
      api.putSettingsSection('tools', { addToCartButtonSelector: selector })
        .then(() => {
          setButtonSelector(selector);
          showToast(tKey('toast.selectorSaved'), 'success');
        })
        .catch((err) => {
          showToast(err.message || tKey('toast.selectorSaveFailed'), 'error');
        })
        .finally(() => setSavingSelector(false));
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [mode, t]);

  const openPicker = () => {
    let parsed;
    try {
      parsed = new URL(pickerUrl.trim());
    } catch {
      showToast(tKey('toast.enterUrl'), 'warning');
      return;
    }
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      showToast(tKey('toast.enterUrl'), 'warning');
      return;
    }
    const popup = window.open(
      api.buttonPickerPageUrl(parsed.toString(), i18n.language),
      'q2c-button-picker',
      'popup=yes,width=1200,height=900',
    );
    if (!popup) {
      showToast(tKey('toast.popupBlocked'), 'warning');
      return;
    }
    pickerWindowRef.current = popup;
  };

  const clearSelector = async () => {
    setSavingSelector(true);
    try {
      await api.putSettingsSection('tools', { addToCartButtonSelector: '' });
      setButtonSelector('');
      showToast(tKey('toast.selectorCleared'), 'success');
    } catch (err) {
      showToast(err.message || tKey('toast.selectorSaveFailed'), 'error');
    } finally {
      setSavingSelector(false);
    }
  };

  const appendProducts = (list, replace) => {
    setProducts((prev) => {
      if (replace) return list;
      const seen = new Set(prev.map((p) => String(p.productId)));
      return [...prev, ...list.filter((p) => !seen.has(String(p.productId)))];
    });
  };

  const resetLoad = () => {
    loadSeq.current += 1;
    setLoadingProducts(false);
    setLoadingMore(false);
    setLoadMode('category');
    setSkuPrefix('');
    setCategoryId(categories[0]?.categoryId ?? '');
    setProducts([]);
    setProductTotal(0);
    setLoadedSource(null);
    setLoaded(false);
    setQuery('');
    setFilter('all');
    setDraftHidden(new Set(hiddenIds));
  };

  const loadProducts = async ({ more = false } = {}) => {
    const mode = more ? loadedSource?.mode : loadMode;
    const prefix = mode === 'sku'
      ? String(more ? loadedSource?.skuPrefix : skuPrefix).trim()
      : '';
    const cat = mode === 'sku' ? '' : (more ? loadedSource?.categoryId : categoryId);
    if (mode === 'sku' && !prefix) {
      showToast(tKey('toast.enterSkuPrefix'), 'warning');
      return;
    }
    if (mode !== 'sku' && !cat) {
      showToast(tKey('toast.selectCategory'), 'warning');
      return;
    }
    const seq = ++loadSeq.current;
    if (more) setLoadingMore(true);
    else setLoadingProducts(true);
    try {
      if (mode === 'sku') {
        const cursor = more ? Number(loadedSource?.nextOffset) || 0 : 0;
        const data = await api.productsBySkuPrefix(prefix, { limit: PAGE_SIZE, offset: cursor });
        if (seq !== loadSeq.current) return;
        const list = Array.isArray(data?.products) ? data.products : [];
        appendProducts(list, !more);
        setLoadedSource({
          mode: 'sku',
          skuPrefix: prefix,
          nextOffset: Number(data?.nextOffset) || 0,
          hasMore: Boolean(data?.hasMore),
        });
        setProductTotal(0);
      } else {
        const offset = more ? products.length : 0;
        const data = await api.productsByCategory(cat, { limit: PAGE_SIZE, offset });
        if (seq !== loadSeq.current) return;
        const list = Array.isArray(data?.products) ? data.products : [];
        const reportedTotal = Number(data?.total);
        const exhausted = more && list.length === 0;
        const nextTotal = exhausted
          ? offset
          : (Number.isFinite(reportedTotal) ? reportedTotal : offset + list.length);
        setProductTotal(nextTotal);
        appendProducts(list, !more);
        setLoadedSource({ mode: 'category', categoryId: String(cat) });
      }
      setLoaded(true);
      if (!more) {
        setQuery('');
        setFilter('all');
      }
    } catch (err) {
      if (seq !== loadSeq.current) return;
      showToast(err.message || tKey('toast.loadProductsFailed'), 'error');
      if (!more) {
        setProducts([]);
        setProductTotal(0);
        setLoaded(false);
        setLoadedSource(null);
      }
    } finally {
      if (seq === loadSeq.current) {
        setLoadingProducts(false);
        setLoadingMore(false);
      }
    }
  };

  const toggleHidden = (productId) => {
    const id = String(productId);
    setDraftHidden((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const rowsInView = () => (catalogHits != null ? catalogHits : products);

  const showAll = () => {
    setDraftHidden((prev) => {
      const next = new Set(prev);
      rowsInView().forEach((p) => next.delete(String(p.productId)));
      return next;
    });
  };

  const hideAll = () => {
    setDraftHidden((prev) => {
      const next = new Set(prev);
      rowsInView().forEach((p) => next.add(String(p.productId)));
      return next;
    });
  };

  useEffect(() => {
    const term = query.trim();
    if (!loaded || !term) {
      catalogSeq.current += 1;
      setCatalogHits(null);
      setCatalogSearching(false);
      return undefined;
    }
    const seq = ++catalogSeq.current;
    const timer = setTimeout(async () => {
      setCatalogSearching(true);
      try {
        const keyword = term.endsWith('*') ? term : `${term}*`;
        const data = await api.searchProducts(keyword);
        if (seq !== catalogSeq.current) return;
        setCatalogHits(Array.isArray(data?.products) ? data.products : []);
      } catch {
        if (seq !== catalogSeq.current) return;
        setCatalogHits([]);
      } finally {
        if (seq === catalogSeq.current) setCatalogSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [query, loaded]);

  const save = async () => {
    setSaving(true);
    try {
      // Merge: keep hidden IDs for products not in the current list,
      // replace visibility for products currently loaded or found by search.
      const loadedIds = new Set([
        ...products.map((p) => String(p.productId)),
        ...(catalogHits || []).map((p) => String(p.productId)),
      ]);
      const kept = hiddenIds
        .map(String)
        .filter((id) => id && !loadedIds.has(id));
      const newlyHidden = [...draftHidden].map(String).filter((id) => id && loadedIds.has(id));
      const nextIds = [...new Set([...kept, ...newlyHidden])];
      await api.putSettingsSection('tools', { [settingsKey]: nextIds });
      setHiddenIds(nextIds);
      setDraftHidden(new Set(nextIds));
      showToast(tKey('toast.saved'), 'success');
    } catch (err) {
      showToast(err.message || tKey('toast.saveFailed'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const clearAllHidden = async () => {
    setSaving(true);
    try {
      await api.putSettingsSection('tools', { [settingsKey]: [] });
      setHiddenIds([]);
      setDraftHidden(new Set());
      showToast(tKey('toast.clearedAll'), 'success');
    } catch (err) {
      showToast(err.message || tKey('toast.clearFailed'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    const prefix = term.endsWith('*') ? term.slice(0, -1) : term;
    const rows = catalogHits != null ? catalogHits : products;
    return rows.filter((p) => {
      const id = String(p.productId);
      const isHidden = draftHidden.has(id);
      if (filter === 'visible' && isHidden) return false;
      if (filter === 'hidden' && !isHidden) return false;
      if (!prefix || catalogHits != null) return true;
      const name = (p.name || '').toLowerCase();
      const sku = (p.sku || '').toLowerCase();
      if (term.endsWith('*')) return name.startsWith(prefix) || sku.startsWith(prefix);
      return name.includes(prefix) || sku.includes(prefix);
    });
  }, [products, catalogHits, draftHidden, filter, query]);

  const visibleCount = products.filter((p) => !draftHidden.has(String(p.productId))).length;
  const hiddenCount = products.length - visibleCount;
  const selectedToHide = products.filter((p) => draftHidden.has(String(p.productId))).length;
  const catalogTotal = productTotal || products.length;
  const sourceMatchesForm = loadedSource?.mode === 'sku'
    ? loadMode === 'sku' && skuPrefix.trim().toLowerCase() === String(loadedSource.skuPrefix || '').toLowerCase()
    : loadMode === 'category' && String(categoryId) === String(loadedSource?.categoryId || '');
  const hasMore = loaded && sourceMatchesForm && (
    loadedSource?.mode === 'sku'
      ? Boolean(loadedSource.hasMore)
      : products.length < catalogTotal
  );
  const totalCount = loadedSource?.mode === 'sku' ? products.length : catalogTotal;

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <div className="q2c-tools-page">
      <h2>{tKey('title')}</h2>

      {mode === 'cart' ? (
        <div className="q2c-tools-card">
          <div className="q2c-tools-card-title">{tKey('pickerTitle')}</div>
          <div className="q2c-tools-card-sub">{tKey('pickerHint')}</div>
          <div className="d-flex flex-wrap gap-2 align-items-end">
            <Form.Group style={{ minWidth: 280, flex: 1 }}>
              <Form.Label className="fw-semibold">{tKey('productUrl')}</Form.Label>
              <Form.Control
                value={pickerUrl}
                placeholder={tKey('productUrlPlaceholder')}
                onChange={(e) => setPickerUrl(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') openPicker();
                }}
              />
            </Form.Group>
            <Button variant="primary" onClick={openPicker} disabled={savingSelector || !pickerUrl.trim()}>
              {tKey('openPage')}
            </Button>
          </div>
          <div className="q2c-picker-saved">
            <span className="fw-semibold">{tKey('pickedLabel')}:</span>{' '}
            <code>{buttonSelector || tKey('noSelector')}</code>
            {buttonSelector ? (
              <Button
                variant="link"
                size="sm"
                className="p-0 ms-2"
                onClick={clearSelector}
                disabled={savingSelector}
              >
                {tKey('clearSelector')}
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}

      <div className="q2c-tools-card">
        <div className="q2c-tools-card-title">{tKey('cardTitle')}</div>
        <div className="q2c-tools-card-sub">
          {tKey('cardSub')}
        </div>

        <div className="d-flex flex-wrap gap-2 align-items-end">
          <Form.Group style={{ minWidth: 160, maxWidth: 200 }}>
            <Form.Label className="fw-semibold">{tKey('filterBy')}</Form.Label>
            <Form.Select value={loadMode} onChange={(e) => setLoadMode(e.target.value)}>
              <option value="category">{tKey('category')}</option>
              <option value="sku">{tKey('skuPrefix')}</option>
            </Form.Select>
          </Form.Group>
          {loadMode === 'sku' ? (
            <Form.Group style={{ minWidth: 240, flex: 1, maxWidth: 360 }}>
              <Form.Label className="fw-semibold">{tKey('skuPrefix')}</Form.Label>
              <Form.Control
                value={skuPrefix}
                placeholder={tKey('skuPrefixPlaceholder')}
                onChange={(e) => setSkuPrefix(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') loadProducts();
                }}
              />
            </Form.Group>
          ) : (
            <Form.Group style={{ minWidth: 240, flex: 1, maxWidth: 360 }}>
              <Form.Label className="fw-semibold">{tKey('category')}</Form.Label>
              <Form.Select value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
                {categories.length === 0 && <option value="">{tKey('noCategories')}</option>}
                {categories.map((c) => (
                  <option key={c.categoryId} value={c.categoryId}>
                    {c.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          )}
          <Button
            variant="primary"
            className="q2c-tools-save-btn"
            onClick={() => loadProducts()}
            disabled={loadingProducts || loadingMore || (loadMode === 'sku' ? !skuPrefix.trim() : !categoryId)}
          >
            {loadingProducts ? tKey('loading') : tKey('loadProducts')}
          </Button>
          <Button
            variant="outline-secondary"
            onClick={resetLoad}
            disabled={
              saving
              || (!loaded && !skuPrefix.trim() && loadMode === 'category' && !loadingProducts && !loadingMore)
            }
          >
            {tKey('reset')}
          </Button>
        </div>
        {loadMode === 'sku' ? (
          <Form.Text className="text-muted d-block mt-2">{tKey('skuPrefixHint')}</Form.Text>
        ) : null}
      </div>

      <div className="q2c-tools-card">
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-2">
          <div>
            <div className="q2c-tools-card-title mb-2">{tKey('buttonTitle')}</div>
            {loaded && (
              <div className="q2c-hide-stats mb-0">
                <span className="q2c-hide-stat visible">
                  <Eye size={16} />
                  {tKey('shown', { count: visibleCount })}
                </span>
                <span className="q2c-hide-stat hidden">
                  <EyeOff size={16} />
                  {tKey('hidden', { count: hiddenCount })}
                </span>
                <span className="q2c-hide-stat total">
                  {loadedSource?.mode === 'sku' && loadedSource.hasMore
                    ? tKey('loadedCount', { count: products.length })
                    : tKey('total', { count: totalCount })}
                </span>
              </div>
            )}
          </div>
          {loaded && (
            <div className="d-flex gap-2">
              <Button variant="success" size="sm" onClick={showAll}>
                {tKey('showAll')}
              </Button>
              <Button variant="danger" size="sm" onClick={hideAll}>
                {tKey('hideAll')}
              </Button>
            </div>
          )}
        </div>

        {!loaded ? (
          <div className="text-center text-muted py-5 border rounded bg-light">
            {tKey('selectPromptBefore')} <strong>{tKey('selectPromptBold')}</strong> {tKey('selectPromptAfter')}
          </div>
        ) : (
          <>
            <div className="q2c-hide-toolbar">
              <Form.Control
                placeholder={tKey('searchPlaceholder')}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              <Form.Select value={filter} onChange={(e) => setFilter(e.target.value)}>
                <option value="all">{tKey('allProducts')}</option>
                <option value="visible">{tKey('buttonShown')}</option>
                <option value="hidden">{tKey('buttonHidden')}</option>
              </Form.Select>
            </div>
            {catalogSearching ? (
              <div className="text-muted small mb-2">{tKey('loading')}</div>
            ) : null}

            {filtered.length === 0 ? (
              <div className="text-center text-muted py-4 border rounded">{tKey('noMatch')}</div>
            ) : (
              <div className="q2c-hide-list">
                {filtered.map((p) => {
                  const id = String(p.productId);
                  const isHidden = draftHidden.has(id);
                  return (
                    <div
                      key={id}
                      className={`q2c-hide-row${isHidden ? ' is-hidden' : ''}`}
                    >
                      <span className="q2c-hide-thumb">
                        {p.imageUrl ? (
                          <img src={p.imageUrl} alt="" />
                        ) : (
                          <span>{(p.name || '?').trim().charAt(0).toUpperCase()}</span>
                        )}
                      </span>
                      <div className="q2c-hide-row-body">
                        <div className="q2c-hide-row-name">{p.name}</div>
                        <div className="q2c-hide-row-sku">{tKey('skuLabel', { sku: p.sku || '—' })}</div>
                      </div>
                      <div className="q2c-hide-switch">
                        <span className={`q2c-hide-pill ${isHidden ? 'hidden' : 'visible'}`}>
                          {isHidden ? tKey('statusHidden') : tKey('statusShown')}
                        </span>
                        <Form.Check
                          type="switch"
                          id={`hide-${mode}-${id}`}
                          checked={isHidden}
                          onChange={() => toggleHidden(id)}
                          aria-label={tKey('hideButton')}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {hasMore ? (
              <div className="q2c-hide-more">
                <span className="q2c-hide-more-note">
                  {loadedSource?.mode === 'sku'
                    ? tKey('showingLoaded', { loaded: products.length })
                    : tKey('showingOf', {
                      loaded: products.length,
                      total: catalogTotal,
                    })}
                </span>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => loadProducts({ more: true })}
                  disabled={loadingMore || loadingProducts}
                >
                  {loadingMore ? tKey('loadingMore') : tKey('loadMore')}
                </Button>
              </div>
            ) : null}

            <div className="q2c-hide-footer">
              <span className="q2c-hide-footer-note">
                {tKey('footerNote', { count: selectedToHide })}
                {hiddenIds.length > 0 ? tKey('savedStoreWide', { count: hiddenIds.length }) : ''}
              </span>
              <div className="d-flex gap-2 flex-wrap">
                {hiddenIds.length > 0 ? (
                  <Button
                    variant="outline-secondary"
                    onClick={clearAllHidden}
                    disabled={saving}
                  >
                    {tKey('clearAllHidden')}
                  </Button>
                ) : null}
                <Button
                  variant="primary"
                  className="q2c-tools-save-btn"
                  onClick={save}
                  disabled={saving}
                >
                  {saving ? tKey('saving') : tKey('saveChanges')}
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default HideProductsPage;
